import React from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { GenericFormPage } from '../../../components/generic';
import { fetcher } from '../../../services/fetcher';
import { spareCaseRefPeekSnapshot } from '../peekRefSnapshot';
import { useSpareCase, SpareCase } from './useData';
import { moduleConfig, getInitialFormData, mapItemToFormData } from './config';

type RefTemplateRow = {
  entityType: string;
  allowManualReferenceEdit?: boolean;
  isActive?: boolean;
  pattern?: string;
};

const Form: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id && id !== 'new');

  const { data: templates } = useSWR<RefTemplateRow[]>('/reference-templates?moduleId=spares', fetcher, {
    revalidateOnFocus: false,
  });
  const allowManualSpareCaseRef =
    templates?.find((r) => r.entityType === 'SPARE_CASE')?.allowManualReferenceEdit !== false;

  const hasActiveSpareCaseTemplate = Boolean(
    templates?.some(
      (r) =>
        r.entityType === 'SPARE_CASE' &&
        r.isActive &&
        typeof r.pattern === 'string' &&
        r.pattern.trim().length > 0,
    ),
  );

  React.useEffect(() => {
    if (!isEdit) {
      spareCaseRefPeekSnapshot.reset();
    }
  }, [isEdit]);

  return (
    <GenericFormPage<SpareCase>
      useData={useSpareCase}
      config={{
        ...moduleConfig,
        getInitialFormData,
        mapItemToFormData,
        form: {
          ...moduleConfig.form,
          create: async (data: Partial<SpareCase>) => {
            const { createSpareCase } = await import('../../../services/mutations');
            const payload = { ...(data as Record<string, unknown>) };
            const cur = String(payload.ref ?? '').trim();
            if (hasActiveSpareCaseTemplate && spareCaseRefPeekSnapshot.shouldOmitRefForServerAllocation(cur)) {
              delete payload.ref;
            }
            return createSpareCase(payload);
          },
        },
      }}
      fieldDisabled={allowManualSpareCaseRef ? undefined : { ref: true }}
    />
  );
};

export default Form;
